import { CellStatusType } from '@store/Spreadsheet/cellChangesSlice';
import React, { useEffect, useMemo, useRef } from 'react';
import { ColumnType, Row } from '../../../../types/Spreadsheet';
import { CutEdges } from './cutEdges';
import styles from './Cell.module.css';
import { Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import CellImage from './CellImage/CellImage';
import CellNumber from './CellNumber/CellNumber';
import CellSelect from './CellSelect/CellSelect';
import CellString from './CellString/CellString';
import CellNumberUnit from './CellNumberUnit/CellNumberUnit';
import CellMultiSelect from './CellMultiSelect/CellMultiSelect';
import CellMultiString from './CellMultiString/CellMultiString';
import CellMultiNumber from './CellMultiNumber/CellMultiNumber';
import CellMultiNumberUnit from './CellMultiNumberUnit/CellMultiNumberUnit';
import CellVideo from './CellVideo/CellVideo';
import { useSpreadsheetConfig } from '../SpreadsheetConfigContext';


export type CellProps = {
  rowId: string;
  columnId: string;
  value: any;
  width?: number;
  columnIndex: number;
  rowIndex: number;
  cellType: ColumnType;
  options?: string[];
  isReadonly?: boolean;
  // Marching-ants edge flags for the cut overlay. `null` means this cell is
  // not part of the active cut range (or no cut is active). Computed by
  // DataRow from `selectCutCellKeySet` and passed down — Cell no longer
  // subscribes to any cut-state selector (TV1-1690 / B-5a).
  cutEdges: CutEdges | null;
  // Full row object passed from DataRow. Replaces the per-cell
  // `selectRowById` subscription (TV1-1690 / B-5b). DataRow already
  // has the row in scope from its own props; piping it down here drops
  // ~1500 per-cell Redux subscriptions on a 5k x 50 grid.
  row: Row;
  // Per-cell facts computed once per row in DataRow and passed as props
  // (TV1-1690 / B-5c). Replaces four per-cell Redux subscriptions that
  // previously ran inside Cell:
  //   - makeSelectIsCellSelected
  //   - makeSelectIsCellFocused
  //   - makeSelectIsCellEditing
  //   - selectCellStatus
  // With this ticket Cell has zero useAppSelector calls — every state-derived
  // value arrives as a prop, and the React.memo comparator below skips
  // re-renders when these primitives are unchanged.
  isSelected: boolean;
  isFocused: boolean;
  isEditing: boolean;
  status: CellStatusType;
  /**
   * TV1-2358 — seed for the editor buffer, set only when this edit was opened by
   * typing over the cell. Computed in DataRow alongside the other per-cell facts
   * so Cell keeps its zero-subscription property.
   */
  editingInitialValue?: string;
};

export type CommonCellProps = {
  rowId: string;
  columnId: string;
  isEditing: boolean;
  value: any;
  currentRow: Row;
  options?: string[];
  /**
   * Seed for the editor's buffer when the edit was opened by typing over the
   * cell (TV1-2358). Present ONLY on that path — a double-click or Enter opens
   * with the cell's own value, as before.
   */
  initialValue?: string;
};

export const Cell: React.FC<CellProps> = ({
  rowId,
  columnId,
  value,
  width,
  rowIndex,
  cellType,
  options,
  isReadonly,
  cutEdges,
  row,
  isSelected,
  isFocused,
  isEditing,
  editingInitialValue,
  status
}) => {
  // console.log( 'rendering cell', rowId, columnId );
  const { t } = useTranslation( 'grid' );
  const cellRef = useRef<HTMLDivElement>( null );
  // `currentRow` is now driven from the `row` prop (TV1-1690 / B-5b).
  // DataRow already holds the row reference; the prior `selectRowById`
  // subscription per cell is removed. Alias kept to minimize churn in
  // the JSX below and in the `CommonCellProps` shape passed to
  // CellString/CellNumber/etc.
  const currentRow = row;
  const { cellTestIdFn } = useSpreadsheetConfig();
  const cellTestId = cellTestIdFn?.({
    rowId,
    columnId,
    itemId: typeof currentRow?.values?.itemId === 'string' ? currentRow.values.itemId : undefined
  });

  useEffect(() => {
    if ( isFocused && cellRef.current && !isEditing ) {
      cellRef.current.focus();
      cellRef.current.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [ isFocused, isEditing, rowIndex ]);

  //classes
  const cellClass = [
    styles.cell,
    isFocused ? styles.focused : '',
    status === 'saved' ? styles.saved : '',
    status === 'unsaved' ? styles.unsaved : '',
    status === 'error' ? styles.error : '',
    isSelected ? isEditing ? '' : styles.selected : '',
    isReadonly ? styles.readonly : ''
  ]
    .filter( Boolean )
    .join( ' ' );

  //style
  const style: React.CSSProperties = useMemo(() => {
    const base: React.CSSProperties = {
      position: 'relative',
      width: `${width || 120}px`,
      minWidth: `${width || 120}px`,
      overflow: 'hidden'
    };
    return base;
  },[width]);

  const componentMapping: Record<ColumnType, React.FC<any>> = {
    string: CellString,
    multiString: CellMultiString,
    number: CellNumber,
    multiNumber: CellMultiNumber,
    numberUnit: CellNumberUnit,
    multiNumberUnit: CellMultiNumberUnit,
    select: CellSelect,
    multiSelect: CellMultiSelect,
    image: CellImage,
    video: CellVideo,
    boolean: CellString //TODO:
  };

  const Component = componentMapping[cellType];

  const cellContent = (
    <div
      ref={cellRef}
      className={cellClass}
      data-row-id={rowId}
      data-column-id={columnId}
      data-testid={cellTestId}
      style={style}
      tabIndex={0}
    >
      <Component
        rowId={rowId}
        columnId={columnId}
        currentRow={currentRow}
        isEditing={isEditing}
        options={options}
        value={value}
        initialValue={isEditing ? editingInitialValue : undefined}
      />

      {/* CUT OVERLAY — edges precomputed by DataRow from `selectCutCellKeySet`
          (TV1-1690 / B-5a). `cutEdges === null` means this cell is outside
          the cut and we render nothing. */}
      {cutEdges?.top && (
        <div
          className={styles.horizontalDash}
          style={{ top: 0 }}
        />
      )}
      {cutEdges?.bottom && (
        <div
          className={styles.horizontalDash}
          style={{ bottom: 0 }}
        />
      )}
      {cutEdges?.left && (
        <div
          className={styles.verticalDash}
          style={{ left: 0 }}
        />
      )}
      {cutEdges?.right && (
        <div
          className={styles.verticalDash}
          style={{ right: 0 }}
        />
      )}
    </div>
  );

  return isReadonly ? (
    <Tooltip label={t( 'readonly_cell_tooltip' )} position="top" withArrow>
      {cellContent}
    </Tooltip>
  ) : cellContent;
};

const cutEdgesEqual = ( a: CutEdges | null, b: CutEdges | null ): boolean => {
  if ( a === b ) return true;
  if ( !a || !b ) return false;
  return a.top === b.top && a.bottom === b.bottom && a.left === b.left && a.right === b.right;
};

export default React.memo(
  Cell,
  ( prevProps, nextProps ) =>
    prevProps.rowId === nextProps.rowId &&
    prevProps.columnId === nextProps.columnId &&
    prevProps.value === nextProps.value &&
    prevProps.width === nextProps.width &&
    prevProps.columnIndex === nextProps.columnIndex &&
    prevProps.rowIndex === nextProps.rowIndex &&
    prevProps.cellType === nextProps.cellType &&
    prevProps.isReadonly === nextProps.isReadonly &&
    // Per-cell facts lifted from Redux into props (TV1-1690 / B-5c). All
    // primitives — strict equality is correct and the comparator only fires
    // when the cell's actual visible state changes.
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isFocused === nextProps.isFocused &&
    prevProps.isEditing === nextProps.isEditing &&
    // TV1-2358 — without this the seeded character would be dropped: the cell
    // re-renders on `isEditing` flipping true, and the comparator would then
    // consider a later change of seed identical.
    prevProps.editingInitialValue === nextProps.editingInitialValue &&
    prevProps.status === nextProps.status &&
    // Reference-equality check is sufficient here: Redux's entity adapter
    // preserves the row reference unless the row's own values change, so an
    // unchanged sibling-cell edit won't cause re-renders elsewhere on the row.
    // If a regression surfaces (e.g. cells re-rendering when sibling cells
    // edit on the same row), switch to a per-column value check:
    //   prevProps.row.values[prevProps.columnId] === nextProps.row.values[nextProps.columnId]
    prevProps.row === nextProps.row &&
    cutEdgesEqual( prevProps.cutEdges, nextProps.cutEdges ) &&
    JSON.stringify( prevProps.options ) === JSON.stringify( nextProps.options )
) as typeof Cell;

Cell.displayName = 'Cell';
